import { useEffect, useState, useRef } from 'react';
import { useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import { createPortal } from 'react-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axiosConfig from '@/data/utils/axiosConfigInstance';
import Toast from '@/utils/toastManager';
import { trackManageSubscriptionEvent } from '@/data/utils/manageSubscriptionAnalytics';
import CancellationRetentionModal from './CancellationRetentionModal';
import MyAccountPlanSelection from './MyAccountPlanSelection';
import MyAccountPaymentMethodModal from './MyAccountPaymentMethodModal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './MyAccountManageSubscriptionModal.css';

const formatUnixDate = unixTimestamp => {
	if (unixTimestamp === null || unixTimestamp === undefined || unixTimestamp === '') return 'Not scheduled';

	try {
		// Accept seconds (e.g., 1680000000), milliseconds (e.g., 1680000000000), Date objects, or ISO strings.
		let dateObj;

		if (unixTimestamp instanceof Date) {
			dateObj = unixTimestamp;
		} else if (typeof unixTimestamp === 'number') {
			// Heuristic: if it's reasonably large (> 1e12) treat as milliseconds, else seconds
			dateObj = new Date(unixTimestamp > 1e12 ? unixTimestamp : unixTimestamp * 1000);
		} else if (typeof unixTimestamp === 'string') {
			const asNumber = Number(unixTimestamp);
			if (!Number.isNaN(asNumber)) {
				dateObj = new Date(asNumber > 1e12 ? asNumber : asNumber * 1000);
			} else {
				dateObj = new Date(unixTimestamp);
			}
		} else {
			return 'Not scheduled';
		}

		if (Number.isNaN(dateObj.getTime())) return 'Not scheduled';

		return dateObj.toLocaleDateString(undefined, { timeZone: 'America/New_York' });
	} catch (e) {
		return 'Not scheduled';
	}
};

const formatDateInputValue = value => {
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const formatLabelText = value =>
	`${value || ''}`
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/\b\w/g, char => char.toUpperCase());

const formatStatusLabel = status => {
	if (!status) return '';
	return formatLabelText(status.toLowerCase());
};

const formatBillingFrequencyLabel = details => {
	const rawFrequency =
		details?.billingFrequency ||
		details?.billingPeriodLabel ||
		details?.billingPeriodUnit ||
		details?.billingPeriod ||
		details?.planPeriod ||
		'';

	const normalizedFrequency = `${rawFrequency}`.toLowerCase().trim();

	if (!normalizedFrequency) return '';
	if (['month', 'monthly'].includes(normalizedFrequency)) return 'Monthly';
	if (['year', 'yearly', 'annual', 'annually'].includes(normalizedFrequency)) return 'Yearly';
	if (['week', 'weekly'].includes(normalizedFrequency)) return 'Weekly';
	if (['day', 'daily'].includes(normalizedFrequency)) return 'Daily';

	return formatLabelText(normalizedFrequency);
};

const formatPlanNameLabel = details => {
	const planSource = [
		details?.planName,
		details?.itemName,
		details?.currentPlanName,
		details?.planLabel,
		details?.plan,
		details?.planId
	]
		.filter(Boolean)
		.join(' ')
		.toLowerCase();

	if (planSource.includes('individual') || planSource.includes('standard')) return 'Standard';
	if (planSource.includes('vip') || planSource.includes('plus')) return 'Plus';
	if (planSource.includes('studio')) return 'Studio';

	const rawPlanName =
		details?.planName || details?.itemName || details?.currentPlanName || details?.planLabel || details?.plan;
	if (rawPlanName)
		return formatLabelText(rawPlanName)
			.replace(/\bPlan\b/gi, '')
			.replace(/\s+/g, ' ')
			.trim();

	if (!details?.planId) return 'Plan unavailable';

	const billingFrequency = formatBillingFrequencyLabel(details).toLowerCase();
	const normalizedPlanId = `${details.planId}`.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
	const sanitizedPlanId = billingFrequency
		? normalizedPlanId
				.replace(new RegExp(`\\b${billingFrequency}\\b`, 'ig'), '')
				.replace(/\s+/g, ' ')
				.trim()
		: normalizedPlanId;

	return formatLabelText(sanitizedPlanId || normalizedPlanId);
};

const getCurrentPlanLabel = details => {
	const planName = formatPlanNameLabel(details);
	const billingFrequency = formatBillingFrequencyLabel(details);

	return billingFrequency ? `${planName} - ${billingFrequency}` : planName;
};

const buildScheduleSummary = details => {
	const messages = [];

	if (details?.hasScheduledPause && details?.pauseDate) {
		messages.push(`Pause scheduled for ${formatUnixDate(details.pauseDate)}`);
	}

	if (details?.hasScheduledCancellation && details?.cancelledAt) {
		messages.push(`Cancellation scheduled for ${formatUnixDate(details.cancelledAt)}`);
	}

	if (details?.resumeDate && details?.status === 'paused') {
		messages.push(`Resume scheduled for ${formatUnixDate(details.resumeDate)}`);
	}

	return messages;
};

const getCurrentTermEndTimestamp = details =>
	details?.currentTermEnd || details?.current_term_end || details?.nextBillingAt || null;

const isPauseScheduledForEndOfTerm = details => {
	const pauseDate = details?.pauseDate;
	const currentTermEnd = getCurrentTermEndTimestamp(details);

	return Boolean(details?.hasScheduledPause && pauseDate && currentTermEnd && pauseDate === currentTermEnd);
};

const getCalculatedResumeDate = (pauseOption, details) => {
	let baseDate = null;

	if (pauseOption === 'immediately') {
		baseDate = new Date();
	} else if (pauseOption === 'end_of_term') {
		const currentTermEnd = getCurrentTermEndTimestamp(details);
		if (currentTermEnd) {
			baseDate = new Date(currentTermEnd * 1000);
		}
	}

	if (!baseDate || Number.isNaN(baseDate.getTime())) return null;

	const resumeDateObj = new Date(baseDate.getTime());
	resumeDateObj.setUTCDate(resumeDateObj.getUTCDate() + 30);
	return resumeDateObj.toLocaleDateString(undefined, {
		timeZone: 'America/New_York'
	});
};

function MyAccountManageSubscriptionModal({ open, onClose, onSubscriptionUpdated, initialAction = null }) {
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [details, setDetails] = useState(null);
	const [actionFeedback, setActionFeedback] = useState(null);
	const [successState, setSuccessState] = useState(null);
	const [activeAction, setActiveAction] = useState(initialAction);
	const [pauseOption, setPauseOption] = useState('immediately');
	const [cancelOption, setCancelOption] = useState('immediately');
	const [cancelDate, setCancelDate] = useState('');
	const [retentionModalOpen, setRetentionModalOpen] = useState(false);
	const [pauseFromRetentionFlow, setPauseFromRetentionFlow] = useState(false);
	const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState(false);
	const [pauseAgreed, setPauseAgreed] = useState(false);
	const [resumeConfirmModalOpen, setResumeConfirmModalOpen] = useState(false);
	const [reverseCancelConfirmOpen, setReverseCancelConfirmOpen] = useState(false);
	const [openDatePicker, setOpenDatePicker] = useState(null);
	const cancelDateInputRef = useRef(null);
	const setUserData = useSetAtom(userInfoAtom);

	const scheduleSummary = buildScheduleSummary(details);
	const currentTermEndTimestamp = getCurrentTermEndTimestamp(details);
	const isIos = details?.channel === 'app_store';
	const effectivePauseDate =
		pauseOption === 'end_of_term' && currentTermEndTimestamp ? formatUnixDate(currentTermEndTimestamp) : 'today';
	const effectiveResumeDate = getCalculatedResumeDate(pauseOption, details) || '30 days from now';

	const navigate = useNavigate();

	useEffect(() => {
		if (!open) return;

		setLoading(true);
		setActiveAction(initialAction);
		setActionFeedback(null);

		axiosConfig
			.get(`${import.meta.env.VITE_APP_API_URL}/chargebee/subscription-management-details`)
			.then(response => {
				setDetails(response.data);
			})
			.catch(error => {
				if (error.response?.status === 404) {
					onClose();
					navigate('/plans');
					return;
				}
				const message = error.response?.data?.error || 'Failed to load subscription management details';
				setActionFeedback({ type: 'error', message });
				Toast.error(message);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [open, initialAction, navigate, onClose]);

	useEffect(() => {
		if (!open) {
			setDetails(null);
			setActiveAction(initialAction);
			setPauseOption('immediately');
			setCancelOption('immediately');
			setCancelDate('');
			setPauseFromRetentionFlow(false);
			setOpenDatePicker(null);
			setActionFeedback(null);
			setSuccessState(null);
			setSubmitting(false);
			setPaymentMethodModalOpen(false);
			setPauseAgreed(false);
			setResumeConfirmModalOpen(false);
			setReverseCancelConfirmOpen(false);
		}
	}, [open, initialAction]);

	useEffect(() => {
		setOpenDatePicker(null);
	}, [activeAction]);

	useEffect(() => {
		if (activeAction === 'cancel' && isPauseScheduledForEndOfTerm(details)) {
			setCancelOption('end_of_term');
			setCancelDate('');
		}
	}, [activeAction, details]);

	useEffect(() => {
		const handleClickOutside = event => {
			if (!openDatePicker) return;

			const target = event.target;
			const isClickOnDatepickerInput =
				target instanceof Element &&
				Boolean(target.closest('.react-datepicker-wrapper, .react-datepicker__input-container'));

			const datepickers = document.querySelectorAll('.react-datepicker');
			let isClickOnDatepicker = false;
			datepickers.forEach(dp => {
				if (dp.contains(event.target)) {
					isClickOnDatepicker = true;
				}
			});

			if (!isClickOnDatepicker && !isClickOnDatepickerInput) {
				setOpenDatePicker(null);
			}
		};

		if (openDatePicker) {
			document.addEventListener('click', handleClickOutside, true);
			return () => document.removeEventListener('click', handleClickOutside, true);
		}
	}, [openDatePicker]);

	if (!open) return null;

	const refreshWithPayload = payload => {
		setDetails(payload);
		// update parent callback
		if (typeof onSubscriptionUpdated === 'function') {
			onSubscriptionUpdated(payload);
		}

		// Keep global user atom and localStorage in sync so other parts of the app
		// (plans page buttons, etc.) render consistently after cancellation/pause/resume.
		try {
			const subscriptionId = payload?.subscriptionId || payload?.id || '';
			const status = (payload?.status || '')?.toLowerCase();
			const hasActive = ['active', 'in_trial', 'non_renewing'].includes(status);

			setUserData(prev => {
				const updatedUser = {
					...prev.user,
					subscription: hasActive,
					isTrialAvailed: payload?.isTrialAvailed ?? prev.user?.isTrialAvailed ?? prev.user?.isTrialAvailed
				};
				const next = {
					...prev,
					subscriptionId: hasActive ? subscriptionId : '',
					user: updatedUser
				};
				try {
					localStorage.setItem('subscriptionId', next.subscriptionId || '');
					localStorage.setItem('userData', JSON.stringify(next.user || {}));
				} catch (e) {
					// ignore localStorage failures
				}
				return next;
			});
		} catch (e) {
			// swallow errors to avoid breaking UI flow
		}
	};

	const showSuccess = (message, title = 'Subscription Updated') => {
		setActionFeedback({ type: 'success', message });
		setSuccessState({
			title,
			message
		});
		setActiveAction(null);
		setRetentionModalOpen(false);
		setResumeConfirmModalOpen(false);
		Toast.success(message);
	};

	const showError = (error, fallbackMessage) => {
		const message = error?.response?.data?.error || error?.response?.data?.message || error?.message || fallbackMessage;

		setActionFeedback({ type: 'error', message });
		Toast.error(message);
	};

	const handlePaymentMethodsClick = () => {
		trackManageSubscriptionEvent({
			eventName: 'payment_methods_clicked',
			subscriptionId: details?.subscriptionId,
			planId: details?.planId,
			source: 'manage_subscription_modal'
		});
		setPaymentMethodModalOpen(true);
	};

	const handlePauseSubmit = async () => {
		trackManageSubscriptionEvent({
			eventName: 'pause_subscription_clicked',
			subscriptionId: details?.subscriptionId,
			planId: details?.planId,
			source: 'manage_subscription_modal',
			meta: { pauseOption }
		});

		setActionFeedback(null);
		setSubmitting(true);

		try {
			const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/pause-subscription`, {
				pauseOption
			});

			trackManageSubscriptionEvent({
				eventName: 'pause_subscription_success',
				subscriptionId: details?.subscriptionId,
				planId: details?.planId,
				source: 'manage_subscription_modal',
				meta: { pauseOption }
			});

			refreshWithPayload(response.data);
			showSuccess(
				pauseOption === 'immediately'
					? 'Subscription paused successfully'
					: 'Subscription pause scheduled successfully',
				pauseOption === 'immediately' ? 'Subscription Paused' : 'Pause Scheduled'
			);
		} catch (error) {
			trackManageSubscriptionEvent({
				eventName: 'pause_subscription_failed',
				subscriptionId: details?.subscriptionId,
				planId: details?.planId,
				source: 'manage_subscription_modal',
				meta: { error: error.message, pauseOption }
			});
			showError(error, 'Failed to pause subscription');
		} finally {
			setSubmitting(false);
		}
	};

	const handleCancelSubmit = async () => {
		const normalizedCancelOption = isPauseScheduledForEndOfTerm(details) ? 'end_of_term' : cancelOption;

		if (normalizedCancelOption === 'specific_date' && !cancelDate) {
			showError(null, 'Select a cancellation date');
			return;
		}

		trackManageSubscriptionEvent({
			eventName: 'cancel_subscription_clicked',
			subscriptionId: details?.subscriptionId,
			planId: details?.planId,
			source: 'manage_subscription_modal',
			meta: { cancelOption: normalizedCancelOption, cancelDate }
		});

		setActionFeedback(null);
		setSubmitting(true);

		try {
			const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/cancel-subscription`, {
				cancelOption: normalizedCancelOption,
				cancelDate: normalizedCancelOption === 'specific_date' ? cancelDate : undefined
			});

			trackManageSubscriptionEvent({
				eventName: 'cancel_subscription_success',
				subscriptionId: details?.subscriptionId,
				planId: details?.planId,
				source: 'manage_subscription_modal',
				meta: { cancelOption: normalizedCancelOption }
			});

			refreshWithPayload(response.data);
			showSuccess(
				normalizedCancelOption === 'immediately'
					? 'Subscription cancelled successfully'
					: 'Subscription cancellation scheduled successfully',
				normalizedCancelOption === 'immediately' ? 'Subscription Cancelled' : 'Cancellation Scheduled'
			);
		} catch (error) {
			trackManageSubscriptionEvent({
				eventName: 'cancel_subscription_failed',
				subscriptionId: details?.subscriptionId,
				planId: details?.planId,
				source: 'manage_subscription_modal',
				meta: { error: error.message, cancelOption: normalizedCancelOption }
			});
			showError(error, 'Failed to cancel subscription');
		} finally {
			setSubmitting(false);
		}
	};

	const handleReverseCancellation = async () => {
		setSubmitting(true);
		try {
			const response = await axiosConfig.post(
				`${import.meta.env.VITE_APP_API_URL}/chargebee/remove-scheduled-cancellation`
			);
			refreshWithPayload(response.data);
			showSuccess('Your scheduled cancellation has been reversed.', 'Cancellation Reversed');
		} catch (error) {
			showError(error, 'Failed to reverse cancellation');
		} finally {
			setSubmitting(false);
			setReverseCancelConfirmOpen(false);
		}
	};

	const handleResumeSubmit = async () => {
		trackManageSubscriptionEvent({
			eventName: 'resume_subscription_clicked',
			subscriptionId: details?.subscriptionId,
			planId: details?.planId,
			source: 'manage_subscription_modal'
		});

		setActionFeedback(null);
		setSubmitting(true);

		try {
			const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/resume-subscription`);

			trackManageSubscriptionEvent({
				eventName: 'resume_subscription_success',
				subscriptionId: details?.subscriptionId,
				planId: details?.planId,
				source: 'manage_subscription_modal'
			});

			refreshWithPayload(response.data);
			showSuccess('Subscription resumed successfully', 'Subscription Resumed');
		} catch (error) {
			trackManageSubscriptionEvent({
				eventName: 'resume_subscription_failed',
				subscriptionId: details?.subscriptionId,
				planId: details?.planId,
				source: 'manage_subscription_modal',
				meta: { error: error.message }
			});
			showError(error, 'Failed to resume subscription');
		} finally {
			setSubmitting(false);
		}
	};

	return createPortal(
		<div className="manage-subscription-modal__overlay">
			<div className="manage-subscription-modal__panel">
				<div className="manage-subscription-modal__header">
					<div>
						<h3>{successState?.title || 'Manage Subscription'}</h3>
						<p>
							{successState?.message
								? 'Your subscription changes were saved successfully.'
								: 'Choose how you want to manage your current subscription.'}
						</p>
					</div>
					<button type="button" className="manage-subscription-modal__close" onClick={onClose}>
						x
					</button>
				</div>

				{loading ? (
					<div className="manage-subscription-modal__state">Loading subscription details...</div>
				) : successState ? (
					<div className="manage-subscription-modal__success-screen">
						<h4>{successState.title}</h4>
						<p>{successState.message}</p>
						<button type="button" className="manage-subscription-modal__submit" onClick={onClose}>
							Done
						</button>
					</div>
				) : !details ? (
					<div className="manage-subscription-modal__state">Subscription details are unavailable right now.</div>
				) : (
					<>
						<div className="manage-subscription-modal__summary">
							<div>
								<span>Current plan</span>
								<strong>{getCurrentPlanLabel(details)}</strong>
							</div>
							<div>
								<span>Status</span>
								<strong>{formatStatusLabel(details.status)}</strong>
							</div>
							<div>
								<span>
									{details.status?.toLowerCase() === 'in_trial'
										? 'Free Trial Ends'
										: ['cancelled', 'non_renewing'].includes(details.status?.toLowerCase())
											? 'Cancellation Date'
											: 'Next Billing'}
								</span>
								<strong>
									{['cancelled', 'non_renewing'].includes(details.status?.toLowerCase())
										? formatUnixDate(details.cancelledAt)
										: formatUnixDate(details.nextBillingAt)}
								</strong>
							</div>
						</div>

						{scheduleSummary.length > 0 && (
							<div className="manage-subscription-modal__notice">
								{scheduleSummary.map(item => (
									<div key={item}>{item}</div>
								))}
							</div>
						)}

						{actionFeedback && (
							<div
								className={`manage-subscription-modal__notice ${
									actionFeedback.type === 'error'
										? 'manage-subscription-modal__notice--error'
										: 'manage-subscription-modal__notice--success'
								}`}
							>
								{actionFeedback.message}
							</div>
						)}

						{activeAction === null && (
							<>
								{details.channel === 'app_store' && (
									<div className="manage-subscription-modal__ios-notice">
										To make changes to your subscription, manage it in your Apple App Store settings.
									</div>
								)}
								<div className="manage-subscription-modal__actions">
									<button
										type="button"
										className="manage-subscription-modal__action-card"
										disabled={isIos || details.status === 'paused'}
										onClick={() => {
											trackManageSubscriptionEvent({
												eventName: 'change_plan_clicked',
												subscriptionId: details?.subscriptionId,
												planId: details?.planId,
												source: 'manage_subscription_modal'
											});
											setActiveAction('plan');
										}}
									>
										<strong>Manage Plan</strong>
										<span>Upgrade, downgrade, or adjust billing.</span>
									</button>

									<button
										type="button"
										className="manage-subscription-modal__action-card"
										onClick={() => {
											trackManageSubscriptionEvent({
												eventName:
													details.status === 'paused' ? 'resume_subscription_clicked' : 'pause_subscription_clicked',
												subscriptionId: details?.subscriptionId,
												planId: details?.planId,
												source: 'manage_subscription_modal'
											});
											if (details.status === 'paused') {
												setResumeConfirmModalOpen(true);
											} else {
												setPauseFromRetentionFlow(false);
												setActiveAction('pause');
											}
										}}
										disabled={isIos || details.status === 'cancelled' || submitting || resumeConfirmModalOpen}
									>
										<strong>{details.status === 'paused' ? 'Resume Subscription' : 'Pause Subscription'}</strong>
										<span>
											{details.status === 'paused'
												? 'Resume billing and reactivate your paused subscription.'
												: 'Temporarily pause your subscription and resume anytime.'}
										</span>
									</button>

									<button
										type="button"
										className="manage-subscription-modal__action-card"
										onClick={handlePaymentMethodsClick}
										disabled={isIos || submitting}
									>
										<strong>Billing Management</strong>
										<span>Manage your payment methods and review previous invoices.</span>
									</button>

									<button
										type="button"
										className={`manage-subscription-modal__action-card${details.hasScheduledCancellation ? ' manage-subscription-modal__action-card--keep' : ' manage-subscription-modal__action-card--danger'}`}
										onClick={() => {
											if (details.hasScheduledCancellation) {
												setReverseCancelConfirmOpen(true);
											} else {
												trackManageSubscriptionEvent({
													eventName: 'cancel_subscription_clicked',
													subscriptionId: details?.subscriptionId,
													planId: details?.planId,
													source: 'manage_subscription_modal'
												});
												setRetentionModalOpen(true);
											}
										}}
										disabled={isIos || details.status === 'cancelled'}
									>
										<strong>{details.hasScheduledCancellation ? 'Keep My Subscription' : 'Cancel Subscription'}</strong>
										<span>
											{details.hasScheduledCancellation
												? 'Your cancellation will be reversed and your plan will continue without any changes to billing or features.'
												: details.status === 'paused'
													? 'Your subscription is paused, so cancelling ends it immediately and your access ends now.'
													: 'End your subscription and retain access until the end of your billing period.'}
										</span>
									</button>
								</div>

								{details.status === 'paused' && (
									<div className="manage-subscription-modal__paused-notice">Resume subscription to manage plan</div>
								)}
							</>
						)}

						{activeAction === 'plan' && (
							<div className="manage-subscription-modal__form manage-subscription-modal__plan-view">
								<button type="button" className="manage-subscription-modal__back" onClick={() => setActiveAction(null)}>
									<ArrowBackIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
									Back
								</button>
								<MyAccountPlanSelection />
							</div>
						)}

						{activeAction === 'pause' && (
							<div className="manage-subscription-modal__form">
								<h4>Pause Subscription</h4>
								<button type="button" className="manage-subscription-modal__back" onClick={() => setActiveAction(null)}>
									<ArrowBackIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
									Back
								</button>
								<div className="manage-subscription-modal__radio-group">
									<label>
										<input
											type="radio"
											checked={pauseOption === 'immediately'}
											onChange={() => setPauseOption('immediately')}
										/>
										Pause immediately
									</label>
									<label>
										<input
											type="radio"
											checked={pauseOption === 'end_of_term'}
											onChange={() => setPauseOption('end_of_term')}
										/>
										Pause at end of current term
									</label>
								</div>

								<p className="manage-subscription-modal__description manage-subscription-modal__description--warning">
									Your subscription will be paused {effectivePauseDate} and{' '}
									<strong className="manage-subscription-modal__description-emphasis">
										will resume on {effectiveResumeDate}
									</strong>
									, at which point your account will return to its regular billing cycle so you can continue enjoying
									access without interruption.
								</p>

								<label className="manage-subscription-modal__checkbox">
									<input type="checkbox" checked={pauseAgreed} onChange={e => setPauseAgreed(e.target.checked)} />
									<span>
										I understand that my subscription will be temporarily paused and will return to its regular billing
										cycle for uninterrupted access on{' '}
										<strong>{getCalculatedResumeDate(pauseOption, details) || '30 days from now'}</strong>.
									</span>
								</label>

								<button
									type="button"
									className="manage-subscription-modal__submit"
									onClick={handlePauseSubmit}
									disabled={submitting || !pauseAgreed}
								>
									{submitting ? 'Saving...' : 'Confirm Pause'}
								</button>
							</div>
						)}

						{activeAction === 'cancel' && (
							<div className="manage-subscription-modal__form">
								<h4>Cancel Subscription</h4>
								<button type="button" className="manage-subscription-modal__back" onClick={() => setActiveAction(null)}>
									<ArrowBackIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
									Back
								</button>
								<div className="manage-subscription-modal__radio-group">
									<label>
										<input
											type="radio"
											checked={cancelOption === 'immediately'}
											onChange={() => setCancelOption('immediately')}
											disabled={isPauseScheduledForEndOfTerm(details)}
										/>
										Cancel immediately
									</label>
									<label>
										<input
											type="radio"
											checked={cancelOption === 'end_of_term'}
											onChange={() => setCancelOption('end_of_term')}
											disabled={details.status === 'paused'}
										/>
										Cancel at end of current term
									</label>
									<label>
										<input
											type="radio"
											checked={cancelOption === 'specific_date'}
											onChange={() => setCancelOption('specific_date')}
											disabled={details.status === 'paused' || isPauseScheduledForEndOfTerm(details)}
										/>
										Cancel on a specific date
									</label>
								</div>

								{isPauseScheduledForEndOfTerm(details) && (
									<div className="manage-subscription-modal__notice">
										This subscription is scheduled to pause at the end of the current term, so cancellation is limited
										to the end of the current term.
									</div>
								)}

								{details.status === 'paused' && (
									<div className="manage-subscription-modal__notice">
										Chargebee only allows paused subscriptions to be cancelled immediately.
									</div>
								)}

								{cancelOption === 'specific_date' && details.status !== 'paused' && (
									<label className="manage-subscription-modal__field">
										<span>Cancellation date</span>
										<DatePicker
											ref={cancelDateInputRef}
											selected={cancelDate ? new Date(`${cancelDate}T00:00:00`) : null}
											onChange={date => {
												setCancelDate(date ? formatDateInputValue(date) : '');
												setOpenDatePicker(null);
											}}
											onCalendarOpen={() => setOpenDatePicker('cancelDate')}
											onCalendarClose={() => setOpenDatePicker(null)}
											onFocus={e => {
												if (openDatePicker === 'cancelDate') {
													e.target.blur();
													setOpenDatePicker(null);
												}
											}}
											minDate={new Date()}
											dateFormat="MMM dd, yyyy"
											placeholderText="Select cancellation date"
											className="manage-subscription-modal__datepicker"
										/>
									</label>
								)}

								{details.status === 'paused' &&
									(() => {
										const isPauseImmediate = !details.pauseDate || details.pauseDate <= Math.floor(Date.now() / 1000);
										return (
											<p className="manage-subscription-modal__description manage-subscription-modal__description--warning">
												{isPauseImmediate
													? 'Your paused subscription will be cancelled immediately. You will lose access to your account right away.'
													: 'Your subscription will be cancelled at the end of your current billing term. You will retain full access until then.'}
											</p>
										);
									})()}

								{details.status !== 'paused' && (
									<p className="manage-subscription-modal__description manage-subscription-modal__description--warning">
										{cancelOption === 'immediately'
											? 'Your subscription will be cancelled immediately. You will lose access to your account right away.'
											: cancelOption === 'end_of_term'
												? 'Your subscription will be cancelled at the end of your current billing term. You will retain full access until then.'
												: `Your subscription will be cancelled on ${cancelDate}. You will retain full access until then.`}
									</p>
								)}

								<button
									type="button"
									className="manage-subscription-modal__submit manage-subscription-modal__submit--danger"
									onClick={handleCancelSubmit}
									disabled={submitting}
								>
									{submitting ? 'Saving...' : 'Confirm Cancellation'}
								</button>
							</div>
						)}
					</>
				)}
			</div>
			<div
				className={`manage-subscription-modal__overlay ${resumeConfirmModalOpen ? 'manage-subscription-modal__overlay--active' : ''}`}
				style={{ display: resumeConfirmModalOpen ? 'flex' : 'none' }}
			>
				<div className="manage-subscription-modal__panel manage-subscription-modal__panel--confirm">
					<h4>Confirm Resume</h4>
					<p>Are you sure you want to resume your subscription? Your billing will resume immediately.</p>
					<div className="manage-subscription-modal__confirm-actions">
						<button
							type="button"
							className="manage-subscription-modal__confirm-cancel"
							onClick={() => setResumeConfirmModalOpen(false)}
							disabled={submitting}
						>
							Cancel
						</button>
						<button
							type="button"
							className="manage-subscription-modal__confirm-submit"
							onClick={async () => {
								setResumeConfirmModalOpen(false);
								await handleResumeSubmit();
							}}
							disabled={submitting}
						>
							{submitting ? 'Resuming...' : 'Resume Subscription'}
						</button>
					</div>
				</div>
			</div>
			{reverseCancelConfirmOpen && (
				<div className="manage-subscription-modal__overlay manage-subscription-modal__overlay--active">
					<div className="manage-subscription-modal__panel manage-subscription-modal__panel--confirm">
						<h4>Keep My Subscription</h4>
						<p>
							Are you sure you want to reverse your scheduled cancellation? Your subscription will return to its
							original billing cycle, with the next renewal on{' '}
							<strong>
								{formatUnixDate(details?.nextBillingAt || details?.currentTermEnd || details?.current_term_end)}
							</strong>
							.
						</p>
						<div className="manage-subscription-modal__confirm-actions">
							<button
								type="button"
								className="manage-subscription-modal__confirm-cancel"
								onClick={() => setReverseCancelConfirmOpen(false)}
								disabled={submitting}
							>
								No, Go Back
							</button>
							<button
								type="button"
								className="manage-subscription-modal__confirm-submit"
								onClick={handleReverseCancellation}
								disabled={submitting}
							>
								{submitting ? 'Reversing...' : "Yes, I'm Sure"}
							</button>
						</div>
					</div>
				</div>
			)}
			<CancellationRetentionModal
				open={retentionModalOpen}
				onClose={() => setRetentionModalOpen(false)}
				onSubscriptionUpdated={payload => refreshWithPayload(payload)}
				onRequestPause={(months = 1) => {
					setRetentionModalOpen(false);
					setPauseFromRetentionFlow(true);
					setPauseOption('immediately');
					setOpenDatePicker(null);
					setActiveAction('pause');
				}}
				subscriptionDetails={details}
				onConfirmCancel={async ({
					retentionId,
					reason,
					otherText,
					likelihood,
					feedback,
					acknowledged,
					couponApplied,
					couponOffered,
					offerType,
					offerOffered,
					offerAccepted,
					noOfferReason,
					pauseMonths,
					isTrialUser,
					isAppleUser,
					cancelOption,
					visitedSteps,
					clientStartedAt,
					clientCompletedAt
				}) => {
					const response = await axiosConfig.post(`${import.meta.env.VITE_APP_API_URL}/chargebee/cancel-subscription`, {
						cancelOption,
						retentionId,
						retentionSurvey: {
							retentionId,
							reason,
							otherText,
							likelihood,
							feedback,
							acknowledged,
							couponApplied,
							couponOffered,
							offerType,
							offerOffered,
							offerAccepted,
							noOfferReason,
							pauseMonths: pauseMonths !== undefined ? pauseMonths : null,
							isTrialUser,
							isAppleUser,
							visitedSteps,
							clientStartedAt,
							clientCompletedAt,
							source: 'cancellation_retention_modal'
						}
					});
					refreshWithPayload(response.data);
					if (response.data?.cancellationSkipped || isAppleUser) {
						showSuccess(
							response.data?.message ||
								'Your feedback has been recorded. Please remember to complete your cancellation in your Apple ID settings.',
							'Feedback Recorded'
						);
					} else {
						showSuccess(
							cancelOption === 'immediately'
								? 'Subscription cancelled successfully'
								: 'Subscription cancellation scheduled successfully',
							cancelOption === 'immediately' ? 'Subscription Cancelled' : 'Cancellation Scheduled'
						);
					}
				}}
			/>
			<MyAccountPaymentMethodModal
				open={paymentMethodModalOpen}
				onClose={() => setPaymentMethodModalOpen(false)}
				subscriptionDetails={details}
				onPaymentMethodUpdated={async () => {
					try {
						const response = await axiosConfig.get(
							`${import.meta.env.VITE_APP_API_URL}/chargebee/subscription-management-details`
						);
						refreshWithPayload(response.data);
					} catch (error) {
						showError(error, 'Failed to refresh subscription details');
					}
				}}
			/>
		</div>,
		document.body
	);
}

export default MyAccountManageSubscriptionModal;
